'use client';

import { ArrowRight, Loader2, Mail } from 'lucide-react';
import Link from 'next/link';
import { useRouter, useSearchParams } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { Suspense, useEffect, useState } from 'react';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { storeMagicLinkNext } from '@/lib/auth/magicLinkNext';
import { apiFetch } from '@/lib/http';
import { useAuthStore } from '@/stores/authStore';

// Friendly messages for the `sso_error` codes the backend redirects with are
// keyed under `auth.loginPage.ssoErrors.<code>`; the opaque auth-store error
// codes (e.g. sso_required, surfaced verbatim by E2-P2) under
// `auth.loginPage.authErrors.<code>`. Both resolve at the display site — the
// store stays untouched — with a fallback for unknown codes.

interface DiscoverResponse {
  sso: boolean;
  start_url?: string;
}

function LoginContent() {
  const t = useTranslations('auth');
  const router = useRouter();
  const searchParams = useSearchParams();
  const login = useAuthStore((s) => s.actions.login);
  const requestMagicLink = useAuthStore((s) => s.actions.requestMagicLink);
  const status = useAuthStore((s) => s.status);
  const error = useAuthStore((s) => s.error);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [magicLinkMode, setMagicLinkMode] = useState(false);
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  // AUTH-04 flow C1: magic-link IS the password-recovery path, and always was —
  // it just was never signposted, so a user who forgot their password had no
  // reason to think "email me a link" was the answer. forgotMode reuses the
  // magic-link machinery verbatim and only reframes the copy: sign in via the
  // link, then change the password in Settings → Security (where the change
  // endpoint accepts the fresh email proof in place of the forgotten password).
  const [forgotMode, setForgotMode] = useState(false);

  // SSO email-first flow (active only when the backend advertises sso_discovery).
  const [configLoading, setConfigLoading] = useState(true);
  const [ssoDiscovery, setSsoDiscovery] = useState(false);
  const [subStep, setSubStep] = useState<'email' | 'password'>('email');
  const [discovering, setDiscovering] = useState(false);
  const [ssoError, setSsoError] = useState<string | null>(() => {
    const code = searchParams.get('sso_error');
    if (!code) return null;
    return t.has(`loginPage.ssoErrors.${code}`)
      ? t(`loginPage.ssoErrors.${code}`)
      : t('loginPage.ssoErrors.sso_internal');
  });

  // Map opaque auth-store error codes to friendly copy at the display site;
  // unknown codes fall through to the raw string.
  const displayError = (error: string): string =>
    t.has(`loginPage.authErrors.${error}`) ? t(`loginPage.authErrors.${error}`) : error;

  const nextParam = searchParams.get('next');
  const safeNextPath =
    nextParam && nextParam.startsWith('/') && !nextParam.startsWith('//') ? nextParam : undefined;

  const isLoading = status === 'loading';

  useEffect(() => {
    let cancelled = false;
    apiFetch<{ sso_discovery?: boolean }>('/api/v2/auth/config')
      .then((cfg) => {
        if (cancelled) return;
        setSsoDiscovery(Boolean(cfg.sso_discovery));
        setConfigLoading(false);
      })
      .catch(() => {
        // If config fetch fails, fall back to the combined form (today's behavior).
        if (!cancelled) setConfigLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  async function handleContinue(e: React.FormEvent) {
    e.preventDefault();
    setSsoError(null);
    setDiscovering(true);
    try {
      const res = await apiFetch<DiscoverResponse>('/api/v2/auth/sso/discover', {
        method: 'POST',
        body: JSON.stringify({ email, ...(safeNextPath && { next: safeNextPath }) }),
      });
      if (res.sso && res.start_url) {
        // Top-level navigation to the IdP — intentionally a hard navigation.
        // No WS singleton exists pre-login, so the internal-nav-must-be-soft
        // rule does not apply here. Keep the spinner while the browser leaves.
        window.location.href = res.start_url;
        return;
      }
    } catch {
      // Fail open: discovery trouble must never block password sign-in.
    }
    setDiscovering(false);
    // One-click auto-login: the email step carries a visually-hidden password
    // field so the browser's password manager fills the full credential pair.
    // If it did (and the domain is not SSO), sign in directly instead of
    // making the user click through the password step they never see.
    if (password) {
      const ok = await login(email, password);
      if (ok) {
        router.replace(safeNextPath ?? '/');
        return;
      }
    }
    setSubStep('password');
  }

  async function handlePasswordLogin(e: React.FormEvent) {
    e.preventDefault();
    setSsoError(null);
    const ok = await login(email, password);
    if (ok) router.replace(safeNextPath ?? '/');
  }

  async function handleMagicLink(e: React.FormEvent) {
    e.preventDefault();
    setSsoError(null);
    const ok = await requestMagicLink(email);
    if (ok) {
      // Hand the validated destination to the verify page. The mailed link
      // opens in a NEW TAB, so this must be localStorage, not sessionStorage —
      // see web/src/lib/auth/magicLinkNext.ts. An absent `next` clears any
      // earlier entry rather than inheriting it.
      storeMagicLinkNext(safeNextPath);
      setMagicLinkSent(true);
    }
  }

  const errorBanners = (
    <>
      {ssoError && (
        <div className="rounded-md bg-destructive/10 border border-destructive/20 px-3 py-2 text-sm text-destructive">
          {ssoError}
        </div>
      )}
      {error && (
        <div className="rounded-md bg-destructive/10 border border-destructive/20 px-3 py-2 text-sm text-destructive">
          {displayError(error)}
        </div>
      )}
    </>
  );

  if (configLoading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-xl font-serif">
          {magicLinkMode
            ? (forgotMode ? t('loginPage.forgotHeading') : t('loginPage.signInWithEmailLink'))
            : t('loginPage.signIn')}
        </CardTitle>
      </CardHeader>

      {magicLinkSent ? (
        <CardContent>
          <div className="flex flex-col items-center gap-3 py-4 text-center">
            <Mail className="h-10 w-10 text-accent" />
            <p className="text-sm text-muted-foreground">
              {t.rich('loginPage.magicLinkSentTo', {
                email,
                strong: (chunks) => <strong>{chunks}</strong>,
              })}
            </p>
            {forgotMode && (
              <p className="text-sm text-muted-foreground">{t('loginPage.forgotSentHint')}</p>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => { setMagicLinkSent(false); setMagicLinkMode(false); setForgotMode(false); }}
            >
              {t('loginPage.backToSignIn')}
            </Button>
          </div>
        </CardContent>
      ) : magicLinkMode ? (
        <form onSubmit={handleMagicLink}>
          <CardContent className="space-y-4">
            {errorBanners}
            {forgotMode && (
              <p className="text-sm text-muted-foreground">{t('loginPage.forgotExplain')}</p>
            )}
            <div className="space-y-2">
              <Label htmlFor="ml-email">{t('loginPage.emailLabel')}</Label>
              <Input
                id="ml-email"
                name="email"
                type="email"
                autoComplete="email"
                placeholder={t('loginPage.emailPlaceholder')}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoFocus
              />
            </div>
          </CardContent>
          <CardFooter className="flex flex-col gap-3">
            <Button type="submit" className="w-full" disabled={isLoading}>
              {isLoading ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Mail className="h-4 w-4 mr-1.5" />}
              {t('loginPage.sendSignInLink')}
            </Button>
            <button
              type="button"
              className="text-sm text-muted-foreground hover:text-foreground transition-colors rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
              onClick={() => { setMagicLinkMode(false); setForgotMode(false); }}
            >
              {t('loginPage.usePasswordInstead')}
            </button>
          </CardFooter>
        </form>
      ) : ssoDiscovery && subStep === 'email' ? (
        <form onSubmit={handleContinue}>
          <CardContent className="space-y-4">
            {errorBanners}
            <div className="space-y-2">
              <Label htmlFor="email">{t('loginPage.emailLabel')}</Label>
              <Input
                id="email"
                name="email"
                type="email"
                autoComplete="username"
                placeholder={t('loginPage.emailPlaceholder')}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoFocus
              />
              {/*
                Visually hidden, but present for the browser's password manager:
                a login form with a lone email field is not recognized as a
                credential form, so saved user/password pairs are never offered
                (the regression introduced by the email-first SSO flow). With
                this field the browser offers + fills the full pair, and
                handleContinue signs in directly when it was filled. sr-only
                (clip, not display:none) keeps it autofillable.
              */}
              <input
                type="password"
                name="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="sr-only"
                tabIndex={-1}
                aria-hidden="true"
              />
            </div>
          </CardContent>
          <CardFooter className="flex flex-col gap-3">
            <Button type="submit" className="w-full" disabled={discovering}>
              {discovering && <Loader2 className="h-4 w-4 animate-spin mr-1.5" />}
              {t('loginPage.continue')}
              <ArrowRight className="h-4 w-4 ml-1.5" />
            </Button>
            <button
              type="button"
              className="text-sm text-muted-foreground hover:text-foreground transition-colors rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
              onClick={() => setMagicLinkMode(true)}
            >
              {t('loginPage.signInWithEmailLinkInstead')}
            </button>
            <p className="text-sm text-muted-foreground">
              {t.rich('loginPage.noAccount', {
                register: (chunks) => (
                  <Link href="/auth/register" className="text-accent hover:underline">{chunks}</Link>
                ),
              })}
            </p>
          </CardFooter>
        </form>
      ) : ssoDiscovery && subStep === 'password' ? (
        <form onSubmit={handlePasswordLogin}>
          <CardContent className="space-y-4">
            {errorBanners}
            <div className="space-y-2">
              <Label htmlFor="email">{t('loginPage.emailLabel')}</Label>
              {/* readOnly, NOT disabled: password managers ignore disabled
                  inputs entirely, which broke autofill on this step. */}
              <Input
                id="email"
                name="email"
                type="email"
                autoComplete="username"
                value={email}
                readOnly
                className="text-muted-foreground"
              />
              <button
                type="button"
                className="text-xs text-muted-foreground hover:text-foreground transition-colors rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                onClick={() => { setPassword(''); setSubStep('email'); }}
              >
                {t('loginPage.useDifferentEmail')}
              </button>
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">{t('loginPage.passwordLabel')}</Label>
              <Input
                id="password"
                name="password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoFocus
              />
                          <button
                type="button"
                className="text-xs text-muted-foreground hover:text-foreground transition-colors rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                onClick={() => { setForgotMode(true); setMagicLinkMode(true); }}
              >
                {t('loginPage.forgotPassword')}
              </button>
            </div>
          </CardContent>
          <CardFooter className="flex flex-col gap-3">
            <Button type="submit" className="w-full" disabled={isLoading}>
              {isLoading && <Loader2 className="h-4 w-4 animate-spin mr-1.5" />}
              {t('loginPage.signIn')}
              <ArrowRight className="h-4 w-4 ml-1.5" />
            </Button>
            <button
              type="button"
              className="text-sm text-muted-foreground hover:text-foreground transition-colors rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
              onClick={() => setMagicLinkMode(true)}
            >
              {t('loginPage.signInWithEmailLinkInstead')}
            </button>
            <p className="text-sm text-muted-foreground">
              {t.rich('loginPage.noAccount', {
                register: (chunks) => (
                  <Link href="/auth/register" className="text-accent hover:underline">{chunks}</Link>
                ),
              })}
            </p>
          </CardFooter>
        </form>
      ) : (
        <form onSubmit={handlePasswordLogin}>
          <CardContent className="space-y-4">
            {errorBanners}
            <div className="space-y-2">
              <Label htmlFor="email">{t('loginPage.emailLabel')}</Label>
              <Input
                id="email"
                name="email"
                type="email"
                autoComplete="username"
                placeholder={t('loginPage.emailPlaceholder')}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoFocus
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">{t('loginPage.passwordLabel')}</Label>
              <Input
                id="password"
                name="password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
                          <button
                type="button"
                className="text-xs text-muted-foreground hover:text-foreground transition-colors rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                onClick={() => { setForgotMode(true); setMagicLinkMode(true); }}
              >
                {t('loginPage.forgotPassword')}
              </button>
            </div>
          </CardContent>
          <CardFooter className="flex flex-col gap-3">
            <Button type="submit" className="w-full" disabled={isLoading}>
              {isLoading && <Loader2 className="h-4 w-4 animate-spin mr-1.5" />}
              {t('loginPage.signIn')}
              <ArrowRight className="h-4 w-4 ml-1.5" />
            </Button>
            <button
              type="button"
              className="text-sm text-muted-foreground hover:text-foreground transition-colors rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
              onClick={() => setMagicLinkMode(true)}
            >
              {t('loginPage.signInWithEmailLinkInstead')}
            </button>
            <p className="text-sm text-muted-foreground">
              {t.rich('loginPage.noAccount', {
                register: (chunks) => (
                  <Link href="/auth/register" className="text-accent hover:underline">{chunks}</Link>
                ),
              })}
            </p>
          </CardFooter>
        </form>
      )}
    </Card>
  );
}

export default function LoginPage() {
  return (
    <Suspense fallback={
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    }>
      <LoginContent />
    </Suspense>
  );
}
